import {
  AccountBalance,
  OptionContract,
  Position,
  Strategy,
} from '@oai-frontend-monorepo/types';
import {
  calculateIntrinsicPrice,
  calculatePositionChange,
  calculateTradeQuote,
  getOptionSymbolsFromPosition,
  isPositionExpired,
  parseUnderlyingSymbolFromPosition,
  symbolIsSPX,
} from '@oai-frontend-monorepo/utils';
import { queryClient } from '../query-client';
import { ensurePositions } from '../queries/usePositions';

export const ensurePositionsTotalPnl = async () => {
  const balance = queryClient.getQueryData<AccountBalance>(['balance']);
  const accountNumber = balance?.balances?.account_number || '';
  const positions = await ensurePositions(accountNumber);

  const filterExpiredPositions = positions?.filter(
    (p) => !isPositionExpired(p, 8)
  );

  const pnl = filterExpiredPositions
    ?.map((positionA) => {
      const positionASymbols = getOptionSymbolsFromPosition(positionA).filter(
        (s) => !!s
      ) as string[];

      const positionAQuotes = positionASymbols
        .map(
          (s): OptionContract =>
            queryClient.getQueryData(['quotes', s]) as unknown as OptionContract
        )
        .filter((q) => !!q);

      if (!positionAQuotes?.length) {
        throw new Error('No quotes found for position');
      }

      const positionAStrategy = positionA?.strategyName as Strategy;

      const positionAQuote = calculateTradeQuote({
        legs: positionAQuotes,
        strategy: positionAStrategy,
        isSell: false, // <-- this is not actually used in the function
      });

      const underlying = parseUnderlyingSymbolFromPosition(positionA);

      const expired = isPositionExpired(positionA, 0, symbolIsSPX(underlying));

      const underlyingQuote = queryClient.getQueryData([
        'quotes',
        underlying,
      ]) as unknown as OptionContract;

      const intrinsicPrice = calculateIntrinsicPrice(
        positionA?.strategyName,
        positionASymbols,
        underlyingQuote?.close as number
      );

      const comparePrice =
        intrinsicPrice !== undefined && expired
          ? intrinsicPrice
          : positionAQuote?.mid;

      const changeSinceOpenA =
        calculatePositionChange({
          oldValue: positionA?.price,
          newValue: comparePrice,
          quantity: positionA?.quantity,
          strategy: positionAStrategy,
        }) ?? 0;

      return {
        pnl: changeSinceOpenA,
        maxLoss: positionA?.maxLoss,
      };
    })
    .reduce(
      (acc, { pnl, maxLoss }) => {
        acc.pnl += pnl;
        acc.maxLoss += maxLoss;
        return acc;
      },
      { pnl: 0, maxLoss: 0 }
    );

  return {
    pnl: pnl?.pnl,
    maxLoss: pnl?.maxLoss,
  };
};
